import { useState, useEffect } from "react";
import { Play, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";


export function VideoGuideButton() {
  const [isOpen, setIsOpen] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 right-4 sm:right-6 z-40 flex flex-col items-center"
      >
        {/* Speech Bubble */}
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          className="relative mb-2.5 w-[130px] bg-white rounded-[14px] shadow-sm border border-gray-100 p-2 text-center"
        >
          <p className="text-[10px] sm:text-[11px] font-semibold text-gray-700 leading-tight">
            Watch a quick guide<br />and get started!
          </p>
          {/* Triangle */}
          <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-b border-r border-gray-100 rotate-45" />
        </motion.div>

        <motion.button
          whileHover={{ scale: 1.025 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => setIsOpen(true)}
          className="
            group relative
            flex items-center
            w-[180px] sm:w-[190px] h-[48px]
            px-1.5
            rounded-full
            bg-white
            border border-[#ed5a29]/15
            shadow-[0_4px_20px_rgba(0,0,0,0.06)]
            hover:shadow-[0_8px_25px_rgba(237,90,41,0.15)]
            transition-all duration-300
            focus:outline-none
            focus:ring-2
            focus:ring-[#ed5a29]/30
          "
          aria-label="Watch Guide"
          title="Watch Guide"
        >
          {/* Attention ring — a single deliberate pulse that repeats on a delay,
              so the CTA calls attention to itself without animating constantly */}
          <motion.span
            aria-hidden="true"
            initial={{ opacity: 0.45, scale: 1 }}
            animate={{ opacity: [0.45, 0], scale: [1, 1.08] }}
            transition={{
              duration: 1.4,
              ease: "easeOut",
              repeat: Infinity,
              repeatDelay: 2.6,
            }}
            className="absolute inset-0 rounded-full border-2 border-[#ed5a29] pointer-events-none"
          />

          {/* Left decorative rays */}
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 pointer-events-none">
            <div className="relative w-4 h-6 text-[#f6a06f]">
              <span className="absolute left-0 top-1/2 w-2 h-[2px] bg-current rounded-full" />
              <span className="absolute left-1.5 top-0 w-2 h-[2px] bg-current rounded-full rotate-45 origin-left" />
              <span className="absolute left-1.5 bottom-0 w-2 h-[2px] bg-current rounded-full -rotate-45 origin-left" />
            </div>
          </div>

          {/* Play button with glow */}
          <div className="relative flex items-center justify-center w-[36px] h-[36px] flex-shrink-0">
            {/* Glow */}
            <motion.div
              animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.95, 1.15, 0.95] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full bg-[#ed5a29] blur-[6px]"
            />
            {/* Circle */}
            <div className="relative z-10 flex items-center justify-center w-full h-full rounded-full bg-[#ed5a29] text-white shadow-sm transition-transform duration-300 group-hover:scale-[1.05]">
              <Play className="w-[14px] h-[14px] fill-current ml-[2px]" />
            </div>
          </div>

          {/* Text & Arrow */}
          <div className="flex-1 flex items-center justify-center pr-1.5">
            <span className="text-[13px] sm:text-[14px] font-semibold text-[#263238]">
              Watch Guide
            </span>
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="ml-1 text-gray-400 group-hover:text-[#ed5a29] transition-colors duration-300"
            >
              →
            </motion.span>
          </div>

          {/* Right decorative rays */}
          <div className="absolute -right-6 top-1/2 -translate-y-1/2 pointer-events-none">
            <div className="relative w-4 h-6 text-[#f6a06f]">
              <span className="absolute right-0 top-1/2 w-2 h-[2px] bg-current rounded-full" />
              <span className="absolute right-1.5 top-0 w-2 h-[2px] bg-current rounded-full -rotate-45 origin-right" />
              <span className="absolute right-1.5 bottom-0 w-2 h-[2px] bg-current rounded-full rotate-45 origin-right" />
            </div>
          </div>
        </motion.button>
      </motion.div>
      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-12">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-[850px] bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-full"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 font-sora">Mobimint Form Builder</h3>
                  <p className="text-sm text-gray-500 font-sans mt-0.5">Quick video guide to get you started</p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#ed5a29]"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Video Player */}
              <div className="relative w-full bg-black aspect-video flex-shrink-0">
                <iframe
                  src="https://www.youtube.com/embed/WuhNqwTsSwE?autoplay=1&mute=1"
                  title="Mobimint Form Builder Guide"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
